import { OS, disableWebgl2TextureAtlas, getGPUDebugRendererInfo, getOS } from "common/utils/context";
import { productLog } from "common/utils/logging";
import type { TierResult } from "detect-gpu";
import { getGPUTier } from "detect-gpu";
import type * as THREE from "three";
import type { Vector3 } from "three";
import { benchmarkGpu } from "./benchmarkGpu";
import { HTTPMaterialFactory } from "./HTTPMaterialFactory";
import { BakeColorTextureMode } from "./materials/designMaterialRuntimeConfig";
import { getPerformanceTierByScore, type PerformanceTier } from "./performanceTiers";
import { SHLightProbeGrid } from "./threejs/higharc/SHLightProbeGrid";
import { SSFXImageRenderer } from "./threejs/higharc/SSFXImageRenderer";
import type { ExtendedWebGLRenderer } from "./threejs/three-extensions";
import { WebGLExtendedCapabilities } from "./WebGLExtendedCapabilities";

interface THREEViewerServicesParams {
  renderer: THREE.WebGLRenderer;
  enableLightOffsetMap: boolean;
  offsetMapLightCount: number;
  onScreenRenderingDisabled?: boolean;
}

export let activeTHREEViewerServices: THREEViewerServices;

export class THREEViewerServices {
  readonly httpMaterialFactory: HTTPMaterialFactory;

  readonly capabilities: WebGLExtendedCapabilities;

  readonly gpuInfo: Promise<TierResult>;

  readonly imageRenderer: SSFXImageRenderer;

  readonly enableLightOffsetMap: boolean;
  readonly offsetMapLightCount: number;
  readonly useWebgl2TextureAtlas: boolean;

  readonly onScreenRenderingDisabled: boolean;

  private hasSetupMaterialFactoryGPUTier: boolean = false;

  constructor({
    renderer,
    enableLightOffsetMap,
    offsetMapLightCount,
    onScreenRenderingDisabled = false,
  }: THREEViewerServicesParams) {
    this.capabilities = new WebGLExtendedCapabilities({ renderer });
    this.enableLightOffsetMap = enableLightOffsetMap;
    this.offsetMapLightCount = offsetMapLightCount;
    this.onScreenRenderingDisabled = onScreenRenderingDisabled;

    // Make sure that we're using consistent three.js renderer settings whenever we're using THREEViewerServices.
    (renderer as ExtendedWebGLRenderer).setFixedMaxSpotLights(0);
    (renderer as ExtendedWebGLRenderer).setFixedMaxPointLights(0);
    (renderer as ExtendedWebGLRenderer).setFixedMaxHemisphereLights(1); // We use one hemisphere light if ssfxRenderer or exterior light probes are disabled.

    const { gpuVendor } = getGPUDebugRendererInfo(renderer.getContext());
    this.useWebgl2TextureAtlas = !disableWebgl2TextureAtlas(gpuVendor);
    this.httpMaterialFactory = new HTTPMaterialFactory(
      renderer,
      true,
      this.useWebgl2TextureAtlas,
      enableLightOffsetMap,
      offsetMapLightCount,
    );
    this.gpuInfo = this.getGpuInfoInternal(renderer);

    this.imageRenderer = new SSFXImageRenderer({
      renderer,
      capabilities: this.capabilities,
      onScreenRenderingDisabled,
    });

    /* eslint-disable-next-line @typescript-eslint/no-this-alias */
    activeTHREEViewerServices = this;
  }

  async getPerformanceTierByScore(): Promise<PerformanceTier> {
    const gpuInfo = await this.gpuInfo;
    const predictedPerformance = gpuInfo.fps !== undefined ? gpuInfo.fps : 30;
    const os = getOS();
    const iPhoneOrAndroidDetected = os === OS.IPHONE || os === OS.ANDROID;
    return getPerformanceTierByScore(predictedPerformance, iPhoneOrAndroidDetected);
  }

  async setupMaterialFactoryGPUTier() {
    if (this.hasSetupMaterialFactoryGPUTier) {
      return;
    }
    this.hasSetupMaterialFactoryGPUTier = true;
    const performanceTier = await this.getPerformanceTierByScore();
    this.httpMaterialFactory.setBakeColorTexture(
      performanceTier.useSimplifiedMaterials ? BakeColorTextureMode.FETCH_FROM_AWS : BakeColorTextureMode.DONT_BAKE,
    );
  }

  // The caller will own the light probe grid and should call dispose on it if needed.
  async createLightProbeGrid(
    probeCountX: number,
    probeCountY: number,
    probeCountZ: number,
    name: string,
    isRefractionProbe: boolean = false,
    simulatedPrismHalfSize?: Vector3,
  ): Promise<SHLightProbeGrid> {
    const performanceTier = await this.getPerformanceTierByScore();
    const generatePmrem = !performanceTier.useSimplifiedMaterials && isRefractionProbe;
    const cubeMapRenderResolution = generatePmrem ? 128 : 12;
    const packedCubeMapResolution = 6;
    const grid = new SHLightProbeGrid(
      probeCountX,
      probeCountY,
      probeCountZ,
      this.capabilities.supportsFloatRenderTarget,
      undefined,
      cubeMapRenderResolution,
      packedCubeMapResolution,
      name,
      undefined,
      undefined,
      generatePmrem,
      simulatedPrismHalfSize,
    );
    return grid;
  }

  private async getGpuInfoInternal(renderer: THREE.WebGLRenderer) {
    const glContext = renderer.getContext();

    const gpuInfo = await getGPUTier({ benchmarksURL: "/static/benchmarks", glContext });

    const iPhoneDetected = getOS() === OS.IPHONE;

    let fps = gpuInfo.fps;

    // A lot of detect-gpu benchmark results seem to be capped at display refresh rate.
    // Most commonly it's either 60 or 144. If it's 144 we don't care since it's above the important thresholds anyway.
    // TODO: Get rid of this workaround if detect-gpu is fixed.
    if (fps === 60) {
      fps = undefined;
    }

    if (iPhoneDetected) {
      // We avoid running the benchmark test on iPhones to avoid extra GPU load.
      // iPhones will always use the same settings either way.
      gpuInfo.fps = fps = 60;
      (gpuInfo.type as string) = "HIGHARC_IPHONE";
    } else if (!fps && gpuInfo.gpu?.toLowerCase()?.includes("apple")) {
      // GPU is obfuscated on Mac Safari, so we just use a default fps.
      // Most Macs should use M series processors, which are fast enough for high settings and high pixel ratio.
      gpuInfo.fps = fps = 150;
      (gpuInfo.type as string) = "HIGHARC_SAFARI";
    }

    if (!fps) {
      gpuInfo.fps = benchmarkGpu(renderer);
      (gpuInfo.type as string) = "HIGHARC_ESTIMATE";
    }

    const supportedWebGLExtensions = glContext.getSupportedExtensions();

    productLog(
      JSON.stringify(
        {
          gpuInfo,
          iPhoneDetected,
          supportedWebGLExtensions,
        },
        undefined,
        "  ",
      ),
    );

    return gpuInfo;
  }

  dispose() {
    this.imageRenderer.dispose();
  }
}
